import { useState } from "react";
import {
  Badge,
  Button,
  Col,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
  UncontrolledTooltip,
} from "reactstrap";
import { Link } from "react-router-dom";
import moment from "moment";
import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";
import type { AttendanceDetails } from "features/EmployeeAttendance/types/employee-attendance.types";
import {
  EmployeeAttendanceStatus,
  EmployeeAttendanceStatusLabelMap,
  EmployeeAttendanceLocationTypeLabelMap,
} from "features/EmployeeAttendance/constants/employee-attendance.constants";

/** Green ≤100 m, amber ≤500 m, red beyond — flags clock-ins that came from a rough location reading. */
function LocationAccuracyBadge({ accuracy }: { accuracy?: number | null }) {
  if (accuracy == null) return null;
  const color = accuracy <= 100 ? "success" : accuracy <= 500 ? "warning" : "danger";
  const label =
    accuracy < 1000 ? `±${Math.round(accuracy)} m` : `±${(accuracy / 1000).toFixed(1)} km`;
  return (
    <span
      className={`ms-1 badge bg-${color}-subtle text-${color}`}
      style={{ fontSize: "0.65rem" }}
      title="Location accuracy reported by the device"
    >
      {label}
    </span>
  );
}

function computeDuration(inTime: string, outTime: string): string | null {
  const inM = moment(inTime);
  const outM = moment(outTime);
  if (!inM.isValid() || !outM.isValid() || outM.isSameOrBefore(inM))
    return null;
  const mins = outM.diff(inM, "minutes");
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

const STATUS_COLOR: Partial<Record<EmployeeAttendanceStatus, string>> = {
  [EmployeeAttendanceStatus.PRESENT]: "success",
  [EmployeeAttendanceStatus.ABSENT]: "danger",
  [EmployeeAttendanceStatus.LEAVE]: "info",
  [EmployeeAttendanceStatus.HOLIDAY]: "primary",
};

const AttendanceViewModal = ({
  show,
  onClose,
  onEdit,
  selectedDate,
  data,
  canEdit,
}: {
  show: boolean;
  onClose: () => void;
  onEdit: () => void;
  selectedDate: string;
  data: AttendanceDetails | null;
  canEdit?: boolean;
}) => {
  const loading = show && data === null;

  const duration =
    data?.totalWorkMinutes != null
      ? `${Math.floor(data.totalWorkMinutes / 60)}h ${data.totalWorkMinutes % 60}m`
      : data?.inTime && data?.outTime
        ? computeDuration(data.inTime, data.outTime)
        : null;

  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);


  const isLeaveManaged =
    data?.status === EmployeeAttendanceStatus.LEAVE && !data?.isHalfDay;

  const inImages = data?.images ?? [];
  const outImages = data?.outImages ?? [];

  const allSessionsList = data
    ? [data, ...data.sessions].filter((s) => Boolean(s.inTime))
    : [];
  const hasMultipleSessions = allSessionsList.length > 1;

  const imageOffsets: { start: number; inCount: number }[] = [];
  const allSlides: { src: string }[] = [];
  allSessionsList.forEach((s) => {
    const sIn = s.images ?? [];
    const sOut = s.outImages ?? [];
    imageOffsets.push({ start: allSlides.length, inCount: sIn.length });
    sIn.forEach((src) => allSlides.push({ src }));
    sOut.forEach((src) => allSlides.push({ src }));
  });
  if (!hasMultipleSessions) {
    // single session: keep original slide order (in then out)
    allSlides.length = 0;
    [...inImages, ...outImages].forEach((src) => allSlides.push({ src }));
  }

  const openLightbox = (index: number) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  return (
    <Modal isOpen={show} toggle={onClose} centered size="lg">
      <ModalHeader toggle={onClose}>
        <span>
          Attendance
          {selectedDate ? (
            <span className="text-muted fw-normal ms-2 small">
              {moment(selectedDate).format("dddd, DD MMM YYYY")}
            </span>
          ) : null}
        </span>
      </ModalHeader>

      <ModalBody>
        {loading ? (
          <div className="text-center py-4">
            <Spinner size="sm" className="me-2" />
            <span className="text-muted">Loading…</span>
          </div>
        ) : !data ? (
          <p className="text-muted mb-0">No attendance record found.</p>
        ) : (
          <Row className="g-3">
            <Col xs={12}>
              <div className="d-flex align-items-center gap-2 flex-wrap">
                <span className="text-muted small fw-medium">Status</span>
                <Badge
                  color={
                    data.isHalfDay &&
                    data.status === EmployeeAttendanceStatus.PRESENT
                      ? "warning"
                      : (STATUS_COLOR[data.status] ?? "secondary")
                  }
                  className="fs-12 px-2 py-1"
                >
                  {data.isHalfDay &&
                  data.status === EmployeeAttendanceStatus.LEAVE
                    ? "Half Day Leave"
                    : data.isHalfDay &&
                        data.status === EmployeeAttendanceStatus.PRESENT
                      ? "Half Day"
                      : (EmployeeAttendanceStatusLabelMap[data.status] ??
                        data.status)}
                </Badge>
                {duration ? (
                  <span className="badge bg-success-subtle text-success border ms-1">
                    {duration}
                  </span>
                ) : null}
              </div>
            </Col>

            {data.leaveApplication ? (
              <Col xs={12}>
                <div className="border rounded-2 p-3 bg-light bg-opacity-50">
                  <div className="d-flex align-items-center justify-content-between gap-2 mb-2">
                    <span
                      className="small fw-semibold text-muted text-uppercase"
                      style={{ letterSpacing: "0.5px" }}
                    >
                      Leave application
                    </span>
                    <Link
                      to={`/hr-management/leave-applications/${data.leaveApplication.id}`}
                      className="small link-primary"
                    >
                      View <i className="ri-arrow-right-line align-bottom" />
                    </Link>
                  </div>
                  <div className="d-flex flex-wrap gap-2 align-items-center">
                    {data.leaveApplication.leaveType ? (
                      <span className="fw-medium text-body small">
                        {data.leaveApplication.leaveType.name}
                        {data.leaveApplication.leaveType.code ? (
                          <span className="text-muted ms-1">
                            ({data.leaveApplication.leaveType.code})
                          </span>
                        ) : null}
                      </span>
                    ) : null}
                    <span className="text-muted small">
                      {moment(data.leaveApplication.fromDate).format(
                        "D MMM YYYY",
                      )}
                      {data.leaveApplication.toDate !==
                        data.leaveApplication.fromDate &&
                      !data.leaveApplication.isHalfDay
                        ? ` → ${moment(data.leaveApplication.toDate).format("D MMM YYYY")}`
                        : null}
                    </span>
                    {data.leaveApplication.isHalfDay ? (
                      <span className="badge bg-warning-subtle text-warning border border-warning-subtle">
                        Half Day
                      </span>
                    ) : null}
                    <span className="badge bg-success-subtle text-success border border-success-subtle">
                      {data.leaveApplication.status}
                    </span>
                  </div>
                </div>
              </Col>
            ) : null}

            {allSessionsList.length > 0 ? (
              <Col xs={12}>
                {hasMultipleSessions ? (
                  <div
                    className="small text-muted fw-semibold text-uppercase mb-2"
                    style={{ letterSpacing: "0.04em" }}
                  >
                    Sessions
                  </div>
                ) : null}
                <div className="vstack gap-2">
                  {allSessionsList.map((s, si) => {
                    const sInLoc = s.inLocation ?? s.location ?? null;
                    const sOutLoc = s.outLocation ?? null;
                    const sInType = s.inLocationType ?? s.locationType ?? null;
                    const sInImgs = s.images ?? [];
                    const sOutImgs = s.outImages ?? [];
                    const sDuration =
                      s.inTime && s.outTime
                        ? computeDuration(s.inTime, s.outTime)
                        : null;
                    const off = imageOffsets[si];
                    return (
                      <div key={si} className="border rounded-2 p-3">
                        <div className="d-flex align-items-center justify-content-between mb-2">
                          <div className="d-flex align-items-center gap-2">
                            {hasMultipleSessions ? (
                              <span
                                className="badge bg-light text-muted border"
                                style={{ fontSize: "0.65rem" }}
                              >
                                #{si + 1}
                              </span>
                            ) : null}
                            <span className="small fw-medium">
                              <i className="ri-login-circle-line text-success me-1" />
                              {moment(s.inTime).format("hh:mm A")}
                              {" → "}
                              {s.outTime ? (
                                <>
                                  <i className="ri-logout-circle-r-line text-danger me-1" />
                                  {moment(s.outTime).format("hh:mm A")}
                                </>
                              ) : (
                                <em className="text-primary">now</em>
                              )}
                            </span>
                          </div>
                          {sDuration ? (
                            <span
                              className="badge bg-light border text-muted"
                              style={{ fontSize: "0.72rem" }}
                            >
                              {sDuration}
                            </span>
                          ) : null}
                        </div>
                        {sInType || sInLoc || sInImgs.length > 0 || s.inIpAddress ? (
                          <div className="mb-2">
                            {s.inIpAddress ? (
                              <div className="small text-muted d-flex align-items-center gap-1 mb-1">
                                <i
                                  className="ri-computer-line flex-shrink-0"
                                  style={{ fontSize: "0.8rem" }}
                                />
                                {s.inIpAddress}
                              </div>
                            ) : null}
                            {sInType || sInLoc ? (
                              <div className="small text-muted d-flex align-items-start gap-1 mb-2">
                                <i
                                  className="ri-map-pin-2-line text-success flex-shrink-0"
                                  style={{ fontSize: "0.8rem", marginTop: 2 }}
                                />
                                <span>
                                  {sInType ? (
                                    <span
                                      className="me-1 badge bg-success-subtle text-success"
                                      style={{ fontSize: "0.65rem" }}
                                    >
                                      {EmployeeAttendanceLocationTypeLabelMap[
                                        sInType
                                      ] ?? sInType}
                                    </span>
                                  ) : null}
                                  {sInLoc}
                                  <LocationAccuracyBadge accuracy={s.inLocationAccuracy} />
                                </span>
                              </div>
                            ) : null}
                            {sInImgs.length > 0 ? (
                              <div className="d-flex flex-wrap gap-2">
                                {sInImgs.map((img, ii) => (
                                  <button
                                    key={`in-${ii}`}
                                    type="button"
                                    className="p-0 border-0 bg-transparent"
                                    onClick={() => openLightbox(off.start + ii)}
                                  >
                                    <img
                                      src={img}
                                      alt={`Session ${si + 1} in ${ii + 1}`}
                                      style={{
                                        width: 48,
                                        height: 48,
                                        objectFit: "cover",
                                        borderRadius: 6,
                                        border: "2px solid #198754",
                                        cursor: "zoom-in",
                                      }}
                                    />
                                  </button>
                                ))}
                              </div>
                            ) : null}
                          </div>
                        ) : null}
                        {s.outTime &&
                        (s.outLocationType || sOutLoc || sOutImgs.length > 0 || s.outIpAddress) ? (
                          <div className="pt-2 border-top">
                            {s.outIpAddress ? (
                              <div className="small text-muted d-flex align-items-center gap-1 mb-1">
                                <i
                                  className="ri-computer-line flex-shrink-0"
                                  style={{ fontSize: "0.8rem" }}
                                />
                                {s.outIpAddress}
                              </div>
                            ) : null}
                            {s.outLocationType || sOutLoc ? (
                              <div className="small text-muted d-flex align-items-start gap-1 mb-2">
                                <i
                                  className="ri-map-pin-2-line text-danger flex-shrink-0"
                                  style={{ fontSize: "0.8rem", marginTop: 2 }}
                                />
                                <span>
                                  {s.outLocationType ? (
                                    <span
                                      className="me-1 badge bg-danger-subtle text-danger"
                                      style={{ fontSize: "0.65rem" }}
                                    >
                                      {EmployeeAttendanceLocationTypeLabelMap[
                                        s.outLocationType
                                      ] ?? s.outLocationType}
                                    </span>
                                  ) : null}
                                  {sOutLoc}
                                  <LocationAccuracyBadge accuracy={s.outLocationAccuracy} />
                                </span>
                              </div>
                            ) : null}
                            {sOutImgs.length > 0 ? (
                              <div className="d-flex flex-wrap gap-2">
                                {sOutImgs.map((img, oi) => (
                                  <button
                                    key={`out-${oi}`}
                                    type="button"
                                    className="p-0 border-0 bg-transparent"
                                    onClick={() =>
                                      openLightbox(off.start + off.inCount + oi)
                                    }
                                  >
                                    <img
                                      src={img}
                                      alt={`Session ${si + 1} out ${oi + 1}`}
                                      style={{
                                        width: 48,
                                        height: 48,
                                        objectFit: "cover",
                                        borderRadius: 6,
                                        border: "2px solid #dc3545",
                                        cursor: "zoom-in",
                                      }}
                                    />
                                  </button>
                                ))}
                              </div>
                            ) : null}
                          </div>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              </Col>
            ) : null}

            {(data.overtimeMinutes ?? 0) > 0 ? (
              <Col xs={6} sm={4}>
                <div className="small text-muted mb-1">Overtime</div>
                <span className="badge bg-warning-subtle text-warning border">
                  {Math.floor(data.overtimeMinutes! / 60)}h{" "}
                  {data.overtimeMinutes! % 60}m
                </span>
              </Col>
            ) : null}

            {data.remarks ? (
              <Col xs={12}>
                <div className="small text-muted mb-1">Remarks</div>
                <div className="small text-body fst-italic">{data.remarks}</div>
              </Col>
            ) : null}

            {data.updated_at ? (
              <Col xs={12}>
                <div className="small text-muted">
                  Last updated{" "}
                  {moment(data.updated_at).format("DD MMM YYYY, hh:mm A")}
                </div>
              </Col>
            ) : null}
          </Row>
        )}
      </ModalBody>

      <ModalFooter>
        <Button color="light" onClick={onClose}>
          Close
        </Button>
        {canEdit && data ? (
          isLeaveManaged ? (
            <>
              <span id="view-modal-edit-btn" className="d-inline-block">
                <Button
                  color="primary"
                  disabled
                  style={{ pointerEvents: "none" }}
                >
                  <i className="ri-pencil-line me-1" />
                  Edit
                </Button>
              </span>
              <UncontrolledTooltip target="view-modal-edit-btn">
                Managed by leave application
              </UncontrolledTooltip>
            </>
          ) : (
            <Button color="primary" onClick={onEdit}>
              <i className="ri-pencil-line me-1" />
              Edit
            </Button>
          )
        ) : null}
      </ModalFooter>

      <Lightbox
        open={lightboxOpen}
        close={() => setLightboxOpen(false)}
        slides={allSlides}
        index={lightboxIndex}
      />
    </Modal>
  );
};

export default AttendanceViewModal;
